import {
  SECTION_DEFAULT_FILL,
  compareCanvasObjectsForRender,
  isDrawableObject,
  type ArrowCorner,
  type CanvasFrame,
  type CanvasObject,
  type EmojiStampKind,
} from "@refinement-canvas/domain";
import { CANVAS_GRID_PX } from "./canvas-coordinates";
import { resolveArrowPath } from "./shape-arrow";
import {
  STICKY_FONT_MAX_PX,
  STICKY_FONT_MIN_PX,
  STICKY_LINE_HEIGHT_RATIO,
  STICKY_PADDING_PX,
  computeBestFitFontSize,
  computeStickyBodyReservePx,
  computeStickySignatureFontPx,
} from "./sticky-font-fit";
import { arrowHeadPoints, computeConnectorRoute } from "./connector-geometry";
import { layoutMarkdownTextForCanvas } from "./markdown-canvas-layout";
import { resolveStickyTextPalette } from "./sticky-text-color";
import { isSnapshotAvatarImageUrl } from "../auth/avatar-image-url";

// PDF 出力用に、キャンバス全体を高解像度の PNG として描画する。
// 既存サムネイル描画（renderCanvasThumbnailDataUrl）の高解像度版で、画像 / 動画オブジェクトについては
// 利用可能であれば画像本体を読み込んで貼り付ける（読み込み失敗時は塗りつぶしのプレースホルダで代替）。
//
// 戻り値: data URL（image/png）。失敗時は空文字列。

// 全体像 PDF の既定の描画幅（A4 横 300DPI 相当。約 3508x1973、16:9 で描画する）。大規模キャンバスで
// 生成 PDF が上限を超える場合は、canvas-pdf-snapshot 側でこの幅を段階的に下げて再描画し上限内へ収める。
export const EXPORT_WIDTH_PX = 3508;
// 自動縮小の下限幅。最低限の可読性を保つため、これ以上は縮小しない。
export const MIN_EXPORT_WIDTH_PX = 1000;
const IMAGE_LOAD_TIMEOUT_MS = 4_000;

export async function renderCanvasExportImage(
  objects: readonly CanvasObject[],
  // 出力対象のキャンバス矩形（サイズプリセットに応じた原点＋寸法）。ラスタはこの矩形そのものを表すため、
  // 矩形の外側（キャンバス外）に置かれたオブジェクトはラスタ端でクリップされ、印刷/PDF はキャンバス範囲のみを含む。
  canvasRect: CanvasFrame,
  exportWidthPx: number = EXPORT_WIDTH_PX,
  // excludeUnpublishedSrc=true のとき、未確定のローカル専用画像（blob: URL）を全体像へ描かない。
  // Connect Note へ外部配信するスナップショットでは、サーバ未公開の本人ローカル画像を外部へ載せないために使う。
  // 利用者自身の手動 PDF エクスポート（既定 false）では、自分の blob プレビューもそのまま出力する。
  options?: { readonly excludeUnpublishedSrc?: boolean | undefined }
): Promise<string> {
  // 大規模キャンバス向けの自動縮小で幅を可変にする。高さ・スケールは幅とキャンバス矩形から比例算出するため、
  // 幅を下げれば全オブジェクト・グリッド・フォントが一様に縮小し、生成 PNG/PDF のサイズも下がる。
  const width = Math.max(1, Math.round(exportWidthPx));
  const height = Math.round((canvasRect.h / canvasRect.w) * width);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // ステージ座標 → ラスタ座標のスケール（現在のキャンバス矩形基準）。
  const scaleX = width / canvasRect.w;
  const scaleY = height / canvasRect.h;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx, width, height, canvasRect, scaleX, scaleY);

  // コメントスレッド（ピン）はビューポート固定の UI 装飾であり、エクスポートには描画しない。
  const drawable = objects.filter((object) => isDrawableObject(object.type));
  // 画像オブジェクト分のみ先に Image を読み込み、ペイント順序を維持するため描画順で整列する
  // （セクション帯 → 通常帯 → 各帯内は zOrder。Web 表示のサブレイヤ分離と同じ規則）。
  const ordered = drawable.slice().sort(compareCanvasObjectsForRender);
  const imageBySrc = await preloadImages(ordered, options?.excludeUnpublishedSrc ?? false);
  // コネクタは端点オブジェクトの現在位置から都度ルーティングするため id 索引を渡す。
  const objectsById = new Map(drawable.map((object) => [object.id, object]));

  for (const object of ordered) {
    ctx.save();
    // キャンバス矩形の原点分だけオフセットしてラスタへ写す（原点が負値の大サイズでも正しく配置される）。
    ctx.translate(
      (object.x - canvasRect.x + object.w / 2) * scaleX,
      (object.y - canvasRect.y + object.h / 2) * scaleY
    );
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY, imageBySrc, objectsById);
    ctx.restore();
  }

  return canvas.toDataURL("image/png");
}

/**
 * 既に開いている印刷用ウィンドウへ、data URL 化した画像を含む HTML を書き込み印刷ダイアログを呼び出す。
 * 利用者は印刷先に「PDF として保存」を選び、PDF ファイルとして書き出す。
 *
 * ウィンドウはポップアップブロックを避けるため、呼び出し元のクリック同期処理で先に
 * `window.open()` して渡す（user activation が有効なうちに開く）。
 * 連打防止は呼び出し元（AppMenu の busy 状態）で行う。
 */
export function openPrintWindowWithImage(
  printWindow: Window,
  dataUrl: string,
  title: string
): void {
  if (!dataUrl) {
    printWindow.close();
    return;
  }
  const sanitizedTitle = escapeHtml(title);
  // ページに対して画像を全幅で配置し、横向き印刷で 1 枚に収まりやすいよう @page を指定する。
  // 印刷後はウィンドウを自動で閉じる。
  const html = `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8" />
<title>${sanitizedTitle}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { display: flex; align-items: center; justify-content: center; min-height: 100vh; }
  img { width: 100%; height: auto; max-width: 100%; }
  @media print {
    body { min-height: 0; }
    img { width: 100%; height: auto; page-break-inside: avoid; }
  }
</style>
</head>
<body>
<img src="${dataUrl}" alt="${sanitizedTitle}" onload="setTimeout(function(){window.focus();window.print();}, 50);" />
<script>
window.addEventListener('afterprint', function(){ window.close(); });
</script>
</body>
</html>`;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}

async function preloadImages(
  objects: readonly CanvasObject[],
  excludeUnpublishedSrc: boolean
): Promise<Map<string, HTMLImageElement>> {
  const srcs = new Set<string>();
  // 未確定のローカル専用画像（blob: URL ＝ createAttachmentPlaceholder の本人用上書き等。サーバ未公開で
  // 他参加者にも配信されない）は、外部配信用スナップショットでは読み込まずプレースホルダへフォールバック
  // させる（drawObject 側で imageBySrc 未ヒット → プレースホルダ）。公開 URL へ finalize 済みなら通常どおり。
  // image.src だけでなく link-card.imageUrl も対象にする（attachment.update / クリップボード経由で blob: が
  // 入り得るうえ、スキーマ上 blob: は許可されているため）。
  const skipUnpublished = (src: string): boolean =>
    excludeUnpublishedSrc && src.startsWith("blob:");
  for (const obj of objects) {
    if (obj.type === "image" && obj.src && !skipUnpublished(obj.src)) srcs.add(obj.src);
    if (obj.type === "link-card" && obj.imageUrl && !skipUnpublished(obj.imageUrl)) {
      srcs.add(obj.imageUrl);
    }
    // アバタースタンプは実画像をエクスポートでも描画し、キャンバス本体と見た目を一致させる。
    // ただし描画側（stamp-object）と同じく「安全なラスタ data:image/ スナップショット」のみ読み込み、
    // それ以外（リモート URL・実行可能 data: 等）は読み込まず 👤 フォールバックさせる
    // （外部 URL 取得によるトラッキング/CORS 汚染の防止と、描画境界の判定一致）。avatarSrc は
    // isSnapshotAvatarImageUrl で安全な data:image/ のみ許可するため blob: は元から除外される。
    if (obj.type === "stamp" && obj.stampKind === "avatar" && obj.avatarSrc && isSnapshotAvatarImageUrl(obj.avatarSrc)) {
      srcs.add(obj.avatarSrc);
    }
  }
  const entries = await Promise.all(
    Array.from(srcs).map(async (src) => {
      const img = await loadImage(src);
      return img ? ([src, img] as const) : null;
    })
  );
  const map = new Map<string, HTMLImageElement>();
  for (const entry of entries) {
    if (entry) map.set(entry[0], entry[1]);
  }
  return map;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    // CORS の絡みで toDataURL がブロックされるリスクを下げるため crossOrigin を付ける。
    // 同一オリジン or 公開バケットからの取得を想定する。失敗時はプレースホルダにフォールバックする。
    img.crossOrigin = "anonymous";
    const timer = window.setTimeout(() => resolve(null), IMAGE_LOAD_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });
}

// object-fit: cover 相当で画像を矩形 (dx,dy,dw,dh) に描画する（短辺基準で中央クロップ）。
// アバタースタンプを丸クリップ内へ歪みなく描くために使う。
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  const iw = img.naturalWidth || dw;
  const ih = img.naturalHeight || dh;
  if (iw <= 0 || ih <= 0) {
    ctx.drawImage(img, dx, dy, dw, dh);
    return;
  }
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  canvasRect: CanvasFrame,
  scaleX: number,
  scaleY: number
): void {
  // グリッド間隔・ドットサイズも描画幅に比例して縮小し、低解像度化してもオブジェクトと同じ縮率で
  // 縮む（全体像の見た目＝グリッド密度を保つ）。既定幅（EXPORT_WIDTH_PX）では従来どおり 80px / 2px。
  const factor = width / EXPORT_WIDTH_PX;
  const step = Math.max(1, 80 * factor);
  const dot = Math.max(1, 2 * factor);
  // 編集画面のグリッド（canvas-grid）と同様に、キャンバス矩形原点の位相ぶんずらして出力物のグリッド
  // 基準位置を編集画面と揃える（大サイズで原点が負値でも一致させる）。
  const phaseX = (((canvasRect.x % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleX;
  const phaseY = (((canvasRect.y % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleY;
  ctx.fillStyle = "rgba(26, 26, 46, 0.08)";
  for (let x = -phaseX; x < width; x += step) {
    for (let y = -phaseY; y < height; y += step) {
      ctx.fillRect(x, y, dot, dot);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number,
  imageBySrc: Map<string, HTMLImageElement>,
  objectsById: Map<string, CanvasObject>
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "section": {
      // セクションは薄い塗り + 枠線 + 左上タイトルで領域を示す（Web 表示・サムネイルと同じ見た目方針）。
      const fill = object.fill ?? SECTION_DEFAULT_FILL;
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
      ctx.strokeStyle = fill;
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      ctx.strokeRect(0, 0, w, h);
      if (object.text.length > 0) {
        // タイトルは枠内左上へ 1 行のみ描画する（タイトルは単一行運用のため折り返し不要）。
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, w, h);
        ctx.clip();
        const fontPx = Math.max(10, 20 * scaleY);
        ctx.fillStyle = "#1a1a2e";
        ctx.font = `600 ${fontPx}px sans-serif`;
        ctx.fillText(object.text, 8 * scaleX, 8 * scaleY + fontPx);
        ctx.restore();
      }
      return;
    }
    case "sticky":
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.4)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      // 本文・署名は付箋の枠でクリップしてから描画する（画面側 sticky-object.tsx の overflow:hidden と同等）。
      // 極小サイズ（padding と署名領域を引くと描画可能領域が 0 以下になる）付箋でも、PDF が枠外へ
      // はみ出して描画されないようにする。
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      // 署名の文字サイズは画面表示（sticky-object.tsx）と同じく付箋サイズに連動させる（スケール前 px）。
      const stickySigFontPx = object.updatedByName
        ? computeStickySignatureFontPx({ boxWidth: object.w, boxHeight: object.h })
        : 0;
      // 背景色に応じた本文・署名の文字色を画面表示（sticky-object.tsx）と同じ判定で解決する
      // （濃い背景では白へ反転）。これにより PDF 変換でも見た目どおりの文字色になる。
      const stickyTextPalette = resolveStickyTextPalette(object.color);
      if (object.text) {
        // 画面表示（sticky-object.tsx）と同様に、付箋サイズへ収まる文字サイズを自動算出する。
        const padX = STICKY_PADDING_PX * scaleX;
        const padY = STICKY_PADDING_PX * scaleY;
        // updatedByName がある付箋は、画面と同じく下部に署名領域（署名占有分＋安全余白）を確保する。
        // 画面表示（sticky-object.tsx）と同じ computeStickyBodyReservePx を使い、本文が欠けず署名とも
        // 重ならない見た目を PDF でも一致させる。
        const reserveY = object.updatedByName
          ? computeStickyBodyReservePx({ boxWidth: object.w, boxHeight: object.h }) * scaleY
          : 0;
        const availWidth = w - padX * 2;
        const availHeight = h - padY * 2 - reserveY;
        const align = object.textAlign ?? "left";
        const fontPx = computeStickyExportFontPx(
          ctx,
          object.text,
          availWidth,
          availHeight,
          scaleY,
          align
        );
        drawMarkdownText(ctx, {
          align,
          baseFontPx: fontPx,
          color: stickyTextPalette.body,
          maxWidth: availWidth,
          text: object.text,
          x: padX,
          y: padY,
        });
      }
      // 画面表示（sticky-object.tsx の左下署名）と見た目を揃えるため、最終更新者名を PDF にも描画する。
      // これにより、上で確保した下部の署名領域（reserveY）が無駄な空白にならず、出力が画面と一致する。
      if (object.updatedByName) {
        drawStickySignature(
          ctx,
          object.updatedByName,
          w,
          h,
          scaleX,
          scaleY,
          stickySigFontPx,
          stickyTextPalette.signature
        );
      }
      ctx.restore();
      return;
    case "shape":
      ctx.strokeStyle = object.stroke ?? "#1a1a2e";
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      drawShape(ctx, object.shapeKind, w, h, object.arrowFromCorner);
      return;
    case "text":
      // 行送りは画面の line-height と同じ 1.5（STICKY_LINE_HEIGHT_RATIO）で描画する
      // （従来の 1.4 から意図的に変更し、画面と PDF の見た目を一致させる）。
      drawMarkdownText(ctx, {
        align: object.textAlign ?? "left",
        baseFontPx: Math.max(10, (object.fontSize ?? 16) * scaleY),
        color: object.color ?? "#1a1a2e",
        maxWidth: w,
        text: object.text,
        x: 0,
        y: 0,
      });
      return;
    case "image": {
      const img = object.src ? imageBySrc.get(object.src) : undefined;
      if (img) {
        ctx.drawImage(img, 0, 0, w, h);
      } else {
        drawMediaPlaceholder(ctx, w, h, "#dbeafe");
      }
      return;
    }
    case "video":
      drawMediaPlaceholder(ctx, w, h, "#111827");
      return;
    case "link-card": {
      drawMediaPlaceholder(ctx, w, h, "#eef2ff");
      const cardImg = object.imageUrl ? imageBySrc.get(object.imageUrl) : undefined;
      if (cardImg) {
        const cardW = w;
        const cardH = h * 0.55;
        ctx.drawImage(cardImg, 0, 0, cardW, cardH);
      }
      ctx.fillStyle = "#1e3a8a";
      ctx.font = `${Math.max(10, 14 * scaleY)}px sans-serif`;
      ctx.fillText(object.title.slice(0, 64), 8 * scaleX, Math.min(h - 8, h * 0.65));
      return;
    }
    case "pen-stroke":
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      object.points.forEach((point, index) => {
        const x = (point.x - object.x) * scaleX;
        const y = (point.y - object.y) * scaleY;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      return;
    case "stamp": {
      // アバタースタンプは、プリロード済みの実アバター画像を丸く描画してキャンバス本体と見た目を
      // 一致させる（複数人のアバターを出力物上でも区別できるようにする）。画像が取得できなかった
      // 場合のみ人物アイコン絵文字へフォールバックする。
      if (object.stampKind === "avatar") {
        // 同一 URL が image / link-card 経由でプリロードされていると、非許可 avatarSrc でも
        // imageBySrc に載って描画されうる。描画直前にも判定し、stamp-object（DOM 描画）と
        // 完全一致させる（安全なラスタ data:image/ のみ描画、それ以外は 👤 へフォールバック）。
        const avatarImg =
          isSnapshotAvatarImageUrl(object.avatarSrc) && object.avatarSrc
            ? imageBySrc.get(object.avatarSrc)
            : undefined;
        if (avatarImg) {
          ctx.save();
          ctx.beginPath();
          // 画面側（stamp-object）は border-radius:50% で、非正方形（w!==h）のとき楕円クリップになる。
          // PDF も同じ楕円クリップへ揃える（正円固定だと横長/縦長スタンプだけ見た目がずれる）。
          ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          // object-fit: cover 相当（短辺基準で中央クロップ）。
          drawImageCover(ctx, avatarImg, 0, 0, w, h);
          ctx.restore();
          return;
        }
      }
      // 絵文字スタンプ（およびアバター画像が取得できなかった場合）はグリフを中央に描画する。
      // フォントは OS のカラー絵文字に委ねる（Canvas2D の描画結果はモノクロになる環境もあるが、
      // PNG 化前提のため最低限読める形にする）。
      const glyph = object.stampKind === "avatar" ? "👤" : STAMP_PDF_GLYPH[object.stampKind];
      const sizePx = Math.max(12, Math.min(w, h) * 0.85);
      ctx.fillStyle = "#1a1a2e";
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      // テキスト系設定をリセットして以降の描画へ影響しないようにする。
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
    case "connector": {
      const fromObj = objectsById.get(object.from.objectId);
      const toObj = objectsById.get(object.to.objectId);
      if (!fromObj || !toObj) return;
      const route = computeConnectorRoute(
        { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h, rotation: fromObj.rotation ?? 0 },
        object.from.side,
        { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h, rotation: toObj.rotation ?? 0 },
        object.to.side
      );
      if (route.length < 2) return;
      // ルートはステージ座標。ctx は object.x/object.y に平行移動済みのため、相対座標へ変換する。
      const toLocal = (p: { x: number; y: number }): { x: number; y: number } => ({
        x: (p.x - object.x) * scaleX,
        y: (p.y - object.y) * scaleY,
      });
      ctx.strokeStyle = object.stroke ?? "#475569";
      ctx.lineWidth = Math.max(1, 2 * Math.min(scaleX, scaleY));
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      route.forEach((point, index) => {
        const l = toLocal(point);
        if (index === 0) ctx.moveTo(l.x, l.y);
        else ctx.lineTo(l.x, l.y);
      });
      ctx.stroke();
      const tip = toLocal(route[route.length - 1]!);
      const before = toLocal(route[route.length - 2]!);
      const head = arrowHeadPoints(tip, before, 12 * Math.min(scaleX, scaleY));
      if (head) {
        ctx.beginPath();
        ctx.moveTo(head.left.x, head.left.y);
        ctx.lineTo(tip.x, tip.y);
        ctx.lineTo(head.right.x, head.right.y);
        ctx.stroke();
      }
      return;
    }
  }
}

// PDF / 印刷出力で使うスタンプ → 絵文字グリフのマッピング。
// 画面表示用（stamp-object.tsx の STAMP_KIND_GLYPH）と同じ値を持つが、
// 循環参照を避けるためにこのモジュール内で複製している。型は EmojiStampKind 基準にして、
// StampKind に絵文字種別が増減したらこのマップもキー追従が必要になるようにする（avatar は対象外）。
const STAMP_PDF_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
};

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number,
  arrowFromCorner?: ArrowCorner | undefined
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 2), Math.max(1, h / 2 - 2), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    // ドラッグ配置の矢印（arrowFromCorner 定義済み）は起点角→対角へ、未指定は従来どおり水平左→右。
    if (arrowFromCorner) {
      const path = resolveArrowPath(w, h, arrowFromCorner, 12, 6);
      ctx.beginPath();
      ctx.moveTo(path.tail.x, path.tail.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.moveTo(path.wingA.x, path.wingA.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.lineTo(path.wingB.x, path.wingB.y);
      ctx.stroke();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(4, h / 2);
    ctx.lineTo(w - 12, h / 2);
    ctx.moveTo(w - 12, h / 2 - 6);
    ctx.lineTo(w - 4, h / 2);
    ctx.lineTo(w - 12, h / 2 + 6);
    ctx.stroke();
    return;
  }
  ctx.fillRect(2, 2, w - 4, h - 4);
  ctx.strokeRect(2, 2, w - 4, h - 4);
}

function drawMediaPlaceholder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: string
): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.35)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}

/**
 * 本文（生の Markdown テキスト）を、画面表示（MarkdownTextView）と同じ整形
 * （見出しの倍率 + 太字 / 箇条書きのマーカー + ぶら下げインデント / 行単位の折り返し）で
 * Canvas 2D へ描画する。レイアウト計算は layoutMarkdownTextForCanvas に集約している。
 */
function drawMarkdownText(
  ctx: CanvasRenderingContext2D,
  options: {
    readonly align: CanvasTextAlign;
    readonly baseFontPx: number;
    readonly color: string;
    readonly maxWidth: number;
    readonly text: string;
    /** 描画領域左上の X（px）。 */
    readonly x: number;
    /** 描画領域左上の Y（px）。 */
    readonly y: number;
  }
): void {
  const layout = layoutMarkdownTextForCanvas({
    align: resolveLayoutAlign(options.align),
    baseFontPx: options.baseFontPx,
    maxWidth: options.maxWidth,
    measure: createCanvasMeasure(ctx),
    text: options.text,
  });
  ctx.fillStyle = options.color;
  for (const segment of layout.segments) {
    ctx.font = canvasFontFor(segment.fontPx, segment.bold);
    ctx.textAlign = segment.anchor;
    ctx.fillText(segment.text, options.x + segment.x, options.y + segment.baselineY);
  }
  // 後続オブジェクトの描画へ寄せ設定を漏らさないよう既定へ戻す。
  ctx.textAlign = "left";
}

/** CanvasTextAlign（start / end を含む）をレイアウトの 3 値アンカーへ丸める。 */
function resolveLayoutAlign(align: CanvasTextAlign): layoutMarkdownTextForCanvas.Anchor {
  return align === "center" || align === "right" ? align : "left";
}

/** レイアウト計算へ注入する幅計測関数（ctx.measureText を包む）。 */
function createCanvasMeasure(ctx: CanvasRenderingContext2D): layoutMarkdownTextForCanvas.Measure {
  return (text, fontPx, bold) => {
    ctx.font = canvasFontFor(fontPx, bold);
    return ctx.measureText(text).width;
  };
}

function canvasFontFor(fontPx: number, bold: boolean): string {
  return `${bold ? "bold " : ""}${fontPx}px sans-serif`;
}

/**
 * 付箋の最終更新者名（左下の署名）を PDF へ描画する。
 * 画面表示（sticky-object.tsx）と同じレイアウト定数（左 8px / 下 4px / 右 32px / 半透明）と
 * 署名文字サイズ（付箋サイズ連動。fontBasePx はスケール前 px）に揃え、
 * 幅を超える場合は省略記号（…）で切り詰める。
 * color には背景色に応じて解決した署名色（濃い背景では白系）を渡す。
 */
function drawStickySignature(
  ctx: CanvasRenderingContext2D,
  name: string,
  w: number,
  h: number,
  scaleX: number,
  scaleY: number,
  fontBasePx: number,
  color: string
): void {
  const sigFontPx = fontBasePx * scaleY;
  ctx.font = `${sigFontPx}px sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  const left = 8 * scaleX;
  // めくれ三角は PDF では描画しないが、画面と同じ折り返し幅にするため右マージン 32px を確保する。
  const right = 32 * scaleX;
  const maxWidth = Math.max(0, w - left - right);
  const text = truncateToWidth(ctx, name, maxWidth);
  if (text.length === 0) return;
  // bottom:4px 相当。alphabetic baseline で下端から少し上に置き、descender が枠外へ出ないようにする。
  ctx.fillText(text, left, h - 5 * scaleY);
}

/**
 * 現在の ctx.font 設定のもとで、テキストが maxWidth に収まるよう末尾を省略記号（…）で切り詰める。
 * 呼び出し前に ctx.font を設定しておくこと。
 */
function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = "…";
  let result = text;
  while (result.length > 0 && ctx.measureText(result + ellipsis).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return result.length > 0 ? result + ellipsis : ellipsis;
}

/**
 * PDF 出力時の付箋本文に使う文字サイズ（スケール後 px）を、利用可能領域へちょうど収まるよう求める。
 * 画面表示（sticky-font-fit.ts）と同じ二分探索アルゴリズムを共有し、Markdown 整形後のレイアウト
 * （見出しの倍率・箇条書きインデントを含む）で高さ・幅を判定する。
 * 戻り値はスケール後の px（drawMarkdownText の baseFontPx へそのまま渡せる）。
 */
function computeStickyExportFontPx(
  ctx: CanvasRenderingContext2D,
  text: string,
  availWidth: number,
  availHeight: number,
  scaleY: number,
  align: CanvasTextAlign
): number {
  if (availWidth <= 0 || availHeight <= 0) return STICKY_FONT_MIN_PX * scaleY;
  const measure = createCanvasMeasure(ctx);
  const layoutAlign = resolveLayoutAlign(align);
  const fits = (fontPx: number): boolean => {
    const layout = layoutMarkdownTextForCanvas({
      align: layoutAlign,
      baseFontPx: fontPx * scaleY,
      maxWidth: availWidth,
      measure,
      text,
    });
    // 高さが収まり、かつ折り返し後の幅も超過しないことを確認する
    // （overflowsWidth は 1 文字も収まらない幅超過を含む。画面側 measureStickyFitFontSize の
    //  scrollWidth 判定と同等）。
    return layout.totalHeight <= availHeight && !layout.overflowsWidth;
  };
  const unscaled = computeBestFitFontSize({
    minPx: STICKY_FONT_MIN_PX,
    maxPx: STICKY_FONT_MAX_PX,
    fits,
  });
  return unscaled * scaleY;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
